import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@lawline/ui/components/alert-dialog";
import { Button } from "@lawline/ui/components/button";
import { Input } from "@lawline/ui/components/input";
import { Label } from "@lawline/ui/components/label";

import { authClient } from "@platform/auth/client";
import { useAuth } from "@platform/auth/context";
import { sessionQueryOptions } from "@platform/auth/guards";

// Account CRUD for the passwordless platform: edit display name, change email
// (confirmed by link), and delete the account (GDPR removal via DB cascades).
export function AccountSection() {
	const t = useTranslations("settings.account");
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const [name, setName] = useState(user?.name ?? "");
	const [email, setEmail] = useState(user?.email ?? "");
	const [nameState, setNameState] = useState<{
		pending: boolean;
		error: string | null;
	}>({ pending: false, error: null });
	const [emailState, setEmailState] = useState<{
		pending: boolean;
		message: string | null;
		error: boolean;
	}>({ pending: false, message: null, error: false });
	const [deleting, setDeleting] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);

	// Adopt server values once the session query resolves / switches user.
	useEffect(() => {
		setName(user?.name ?? "");
		setEmail(user?.email ?? "");
	}, [user?.name, user?.email]);

	const nameDirty = name.trim() !== "" && name.trim() !== (user?.name ?? "");
	const emailDirty = email.trim() !== "" && email.trim() !== (user?.email ?? "");

	async function saveName() {
		setNameState({ pending: true, error: null });
		const { error } = await authClient.updateUser({ name: name.trim() });
		if (error) {
			setNameState({
				pending: false,
				error: error.message ?? t("name.error"),
			});
			return;
		}
		await queryClient.invalidateQueries({
			queryKey: sessionQueryOptions.queryKey,
		});
		setNameState({ pending: false, error: null });
	}

	async function changeEmail() {
		setEmailState({ pending: true, message: null, error: false });
		const { error } = await authClient.changeEmail({
			newEmail: email.trim(),
			callbackURL: `${window.location.origin}/settings`,
		});
		setEmailState({
			pending: false,
			error: Boolean(error),
			message: error ? (error.message ?? t("email.error")) : t("email.success"),
		});
	}

	async function deleteAccount() {
		setDeleting(true);
		setDeleteError(null);
		const { error } = await authClient.deleteUser();
		if (error) {
			setDeleting(false);
			setDeleteError(error.message ?? t("delete.error"));
			return;
		}
		window.location.href = "/login";
	}

	return (
		<section className="flex flex-col gap-6">
			<h2 className="text-muted-foreground text-lg font-medium">{t("heading")}</h2>

			<div className="flex flex-col gap-2">
				<Label htmlFor="name">{t("name.label")}</Label>
				<div className="flex gap-2">
					<Input
						id="name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						className="max-w-xs"
					/>
					<Button onClick={saveName} disabled={!nameDirty || nameState.pending}>
						{nameState.pending ? t("name.saving") : t("name.save")}
					</Button>
				</div>
				{nameState.error && <p className="text-destructive text-sm">{nameState.error}</p>}
			</div>

			<div className="flex flex-col gap-2">
				<Label htmlFor="email">{t("email.label")}</Label>
				<div className="flex gap-2">
					<Input
						id="email"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						className="max-w-xs"
					/>
					<Button
						variant="outline"
						onClick={changeEmail}
						disabled={!emailDirty || emailState.pending}
					>
						{emailState.pending ? t("email.sending") : t("email.change")}
					</Button>
				</div>
				{emailState.message && (
					<p
						className={
							emailState.error ? "text-destructive text-sm" : "text-muted-foreground text-sm"
						}
					>
						{emailState.message}
					</p>
				)}
			</div>

			<div className="flex flex-col gap-2">
				<AlertDialog>
					<AlertDialogTrigger asChild>
						<Button variant="destructive" className="w-fit">
							{t("delete.button")}
						</Button>
					</AlertDialogTrigger>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>{t("delete.dialogTitle")}</AlertDialogTitle>
							<AlertDialogDescription>{t("delete.dialogDescription")}</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>{t("delete.cancel")}</AlertDialogCancel>
							<AlertDialogAction variant="destructive" onClick={deleteAccount} disabled={deleting}>
								{deleting ? t("delete.deleting") : t("delete.confirm")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
				{deleteError && <p className="text-destructive text-sm">{deleteError}</p>}
			</div>
		</section>
	);
}
